<template>
  <v-container fluid>
    <v-row dense>
      <v-col cols="auto">
        <Selector
        :label="$t('category')"
        :items="tractCategories"
        @item-selected="categoryChosen"/>
      </v-col>

      <v-col cols="auto">
        <Selector
        label="Entities"
        :items="entities"
        @item-selected="entitySelected"
        style="max-width:16rem"
        itemText="name"/>
      </v-col>

      <v-col cols="auto">
        <TimeframePicker
        @date-range-chosen="dateRangeChosen"/>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" lg="9" xl="10" align="end" class="pr-4" v-if="productCosts.productSummaries.length > 0 || speciesCosts.speciesSummaries.length > 0">
        <Icon
          :icon="showSpeciesView ? 'mdi-pine-tree-variant' : 'mdi-leaf-maple'"
          @icon-clicked="switchSpeciesViewMode"
          :small="false"
          :tooltipText="showSpeciesView ? $t('showProductsView') : $t('showSpeciesView')"
          margin="mr-1"
        />
      </v-col>
      <v-col cols="12" lg="9" xl="10">
        <ProductSummaryTable v-if="productCosts.productSummaries.length > 0 && !showSpeciesView" :productSummary="productCosts.productSummaries"/>
        <SpeciesSummaryTable v-else-if="speciesCosts.speciesSummaries.length > 0 && showSpeciesView" :speciesSummary="speciesCosts.speciesSummaries"/>
      </v-col>

      <v-col cols="12" lg="3" xl="2">
        <ProductSummaryChart v-if="productCosts.productSummaries.length > 0 && !showSpeciesView" :productSummary="productCosts.productSummaries"/>
        <SpeciesSummaryChart v-else-if="speciesCosts.speciesSummaries.length > 0 && showSpeciesView" :speciesSummary="speciesCosts.speciesSummaries"/>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" class="pl-4 pr-4">
        <v-row align="center">
          <v-col>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('search')"
              single-line
              clearable
              color="primary"
            />
          </v-col>

          <v-col cols="auto">
            <v-row dense>
              <v-col cols="auto" v-if="showSupplierIcon">
                <Icon
                  elementId="tract-supplier-toggle"
                  :icon="groupBySupplier ? 'mdi-crosshairs-gps' : 'mdi-account'"
                  :small="false"
                  :tooltipText="groupBySupplier ? $t('showAllTracts') : $t('groupBySupplier')"
                  margin="mr-1"
                  @icon-clicked="switchSupplierViewMode"
                />
              </v-col>

              <v-col cols="auto">
                <ColumnAdjuster
                  :propColumnSet.sync="columnSet"
                  @new-columns-selected="newColumnsSelected"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <ProductSummaryByTract
          v-if="!showSupplierTable"
          :tractSummaries="showSpeciesView ? speciesCosts.tractSummaries : productCosts.tractSummaries"
          :entity="selectedEntity"
          :search="search"
          :columnSet="columnSet"
          :dateRange="dateRange"
          :chosenCategory="chosenCategory"
          :speciesMode="showSpeciesView"
        />
        <DeliveredCostsBySupplier
          v-else
          :supplierSummaries="showSpeciesView ? speciesDeliveredCostsBySupplier : productDeliveredCostsBySupplier"
          :columnSet="showSpeciesView ? speciesColumnSetBySupplier : supplierColumnSet"
          :search="search"
          :entity="selectedEntity"
          :dateRange="dateRange"
          :chosenCategory="chosenCategory"
          :speciesMode="showSpeciesView"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { formatMoney, tonStringFromPounds } from '@/utils/NumericMutations.js'
import { getDateRangeForPeriod } from '@/utils/DateFormatter.js'
import { TractTypeCategory } from '@/utils/Enumerations.js'
import TractHeaders from '@/headers/Tract.js'
import { groupBy, sumBy } from 'lodash'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { localeCompareAlphanumeric } from '@/utils/base'

export default {
  name: 'ProductCosts',

  data: () => ({
    loading: false,
    productCosts: {
      productSummaries: [],
      tractSummaries: []
    },
    speciesCosts: {
      speciesSummaries: [],
      tractSummaries: []
    },
    selectedEntity: undefined,
    tractCategories: TractTypeCategory.names,
    dateRange: undefined,
    chosenCategory: 0,
    columnSetStumpage: null,
    columnSetDelivered: null,
    supplierColumnSet: null,
    speciesColumnSetBySupplier: null,
    speciesColumnSetStumpage: null,
    speciesColumnSetDelivered: null,
    groupBySupplier: false,
    productDeliveredCostsBySupplier: [],
    speciesDeliveredCostsBySupplier: [],
    search: '',
    showSpeciesView: false
  }),

  components: {
    Selector: () => import('@/components/core/Selector.vue'),
    TimeframePicker: () => import('@/components/tract/tract-analysis/TimeframePicker.vue'),
    ProductSummaryChart: () => import('@/components/settlements/accounts-payable-module/production-summary/ProductSummaryChart.vue'),
    ProductSummaryTable: () => import('@/components/settlements/accounts-payable-module/production-summary/ProductSummaryTable.vue'),
    SpeciesSummaryTable: () => import('@/components/settlements/accounts-payable-module/production-summary/SpeciesSummaryTable.vue'),
    SpeciesSummaryChart: () => import('@/components/settlements/accounts-payable-module/production-summary/SpeciesSummaryChart.vue'),
    DeliveredCostsBySupplier: () => import('@/components/tract/tract-analysis/DeliveredCostsBySupplier.vue'),
    ProductSummaryByTract: () => import('@/components/tract/tract-analysis/ProductSummaryByTract.vue'),
    ColumnAdjuster: () => import('@/components/core/ColumnAdjuster.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  computed: {
    ...mapGetters('user', ['businessEntities']),
    tableActions () {
      return [
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh'),
          action: this.refresh
        }
      ]
    },

    showSupplierIcon () {
      return this.chosenCategory === TractTypeCategory.Delivered.value
    },

    entities () {
      if (!this.businessEntities) return []
      const businessEntities = JSON.parse(JSON.stringify(this.businessEntities))
      if (this.chosenCategory === 1) {
        businessEntities.unshift({
          name: 'All Entities',
          businessEntityId: null
        })
      }
      return businessEntities
    },

    showSupplierTable () {
      return this.groupBySupplier && this.chosenCategory === TractTypeCategory.Delivered.value
    },

    columnSet: {
      get  () {
        if (this.showSpeciesView) {
          return this.showSupplierTable
            ? this.speciesColumnSetBySupplier
            : this.chosenCategory === TractTypeCategory.Stumpage.value
              ? this.speciesColumnSetStumpage
              : this.speciesColumnSetDelivered
        }
        return this.showSupplierTable
          ? this.supplierColumnSet
          : this.chosenCategory === TractTypeCategory.Stumpage.value
            ? this.columnSetStumpage
            : this.columnSetDelivered
      },
      set (columnSet) {
        if (this.showSupplierTable && this.showSpeciesView) {
          this.speciesColumnSetBySupplier = columnSet
        } else if (this.showSupplierTable && !this.showSpeciesView) {
          this.supplierColumnSet = columnSet
        } else if (this.chosenCategory === TractTypeCategory.Stumpage.value && this.showSpeciesView) {
          this.speciesColumnSetStumpage = columnSet
        } else if (this.chosenCategory === TractTypeCategory.Stumpage.value && !this.showSpeciesView) {
          this.columnSetStumpage = columnSet
        } else if (this.chosenCategory === TractTypeCategory.Delivered.value && this.showSpeciesView) {
          this.speciesColumnSetDelivered = columnSet
        } else if (this.chosenCategory === TractTypeCategory.Delivered.value && !this.showSpeciesView) {
          this.columnSetDelivered = columnSet
        }
      }
    }
  },

  async created () {
    this.columnSetStumpage = TractHeaders.tractSummaryHeaders(TractTypeCategory.Stumpage.value)
    this.columnSetDelivered = TractHeaders.tractSummaryHeaders(TractTypeCategory.Delivered.value)
    this.supplierColumnSet = TractHeaders.tractCostsDeliveredBySupplierHeaders()
    this.speciesColumnSetStumpage = TractHeaders.tractSummaryHeadersBySpecies(TractTypeCategory.Stumpage.value)
    this.speciesColumnSetDelivered = TractHeaders.tractSummaryHeadersBySpecies(TractTypeCategory.Delivered.value)
    this.speciesColumnSetBySupplier = TractHeaders.tractCostsBySpeciesBySupplierHeaders()
    this.groupBySupplier = localStorage.getItem(LocalStorageKeys.PRODUCT_COSTS_GROUP_BY_SUPPLIER) === 'true'
    this.showSpeciesView = localStorage.getItem(LocalStorageKeys.PRODUCT_COSTS_SHOW_SPECIES_VIEW) === 'true'
    await this.fetchAllBusinessEntities()
    this.chosenCategory = TractTypeCategory.Stumpage.value
    this.chosenEntity = this.entities[0]
  },

  methods: {
    ...mapActions('tract', ['getTractProductCosts', 'getDetailedTractProductCosts', 'getTractSpeciesCosts']),
    ...mapActions('user', ['fetchAllBusinessEntities']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    formatMoney,
    tonStringFromPounds,
    getDateRangeForPeriod,

    entitySelected (entity) {
      if (JSON.stringify(entity) === JSON.stringify(this.selectedEntity)) return
      this.selectedEntity = entity
      this.refresh()
    },

    switchSupplierViewMode () {
      this.groupBySupplier = !this.groupBySupplier
      const localStorageSetting = this.groupBySupplier ? 'true' : 'false'
      localStorage.setItem(LocalStorageKeys.PRODUCT_COSTS_GROUP_BY_SUPPLIER, localStorageSetting)
    },

    switchSpeciesViewMode () {
      this.showSpeciesView = !this.showSpeciesView
      const localStorageSetting = this.showSpeciesView ? 'true' : 'false'
      localStorage.setItem(LocalStorageKeys.PRODUCT_COSTS_SHOW_SPECIES_VIEW, localStorageSetting)
    },

    categoryChosen (category) {
      if (TractTypeCategory.fromInt(this.chosenCategory) === category) return
      this.chosenCategory = TractTypeCategory.toInt(category)
      this.$emit('tract-category-selected', this.chosenCategory)
      this.refresh()
    },

    dateRangeChosen (dateRange) {
      if (this.dateRange?.sinceTime === dateRange.sinceTime && this.dateRange?.untilTime === dateRange.untilTime) return
      this.dateRange = dateRange
      this.refresh()
    },

    getCSVObjects (deliveredCosts) {
      return deliveredCosts.map(dc => {
        return {
          name: dc.tractName,
          supplier: dc.accountName,
          type: dc.tractType,
          plf: dc.plf,
          loads: dc.loads,
          grossTons: dc.grossTons,
          netTons: dc.netTons,
          costPerTon: dc.costPerTon,
          totalCost: dc.totalCost
        }
      })
    },

    newColumnsSelected (columnSet) {
      this.columnSet = columnSet
    },

    async refresh () {
      if (!this.dateRange) return
      if (!this.selectedEntity) return
      this.loading = true
      try {
        const response = await this.getTractProductCosts({
          category: this.chosenCategory,
          businessEntityId: this.selectedEntity.businessEntityId,
          ...this.dateRange
        })
        this.productCosts = response
        this.productDeliveredCostsBySupplier = this.mapTractSummariesBySupplier(response.tractSummaries, false)

        const speciesResponse = await this.getTractSpeciesCosts({
          category: this.chosenCategory,
          businessEntityId: this.selectedEntity.businessEntityId,
          ...this.dateRange
        })
        this.speciesCosts = speciesResponse
        this.speciesDeliveredCostsBySupplier = this.mapTractSummariesBySupplier(speciesResponse.tractSummaries, true)
      } finally {
        this.loading = false
      }
    },

    mapTractSummariesBySupplier (tractSummaries, bySpecies) {
      const tractSummariesGrouped = groupBy(tractSummaries, 'supplier')
      return bySpecies ? Object.keys(tractSummariesGrouped).map(td => {
        return {
          supplier: td !== null && td !== undefined && td !== 'null' ? td : this.$t('noAssignedSupplier'),
          percentageOfTotal: tractSummariesGrouped[td].reduce((total, t) => total + (t.percentageOfTotal || 0), 0),
          grossMbf: tractSummariesGrouped[td].reduce((total, t) => total + (t.grossMbf || 0), 0),
          netMbf: tractSummariesGrouped[td].reduce((total, t) => total + (t.netMbf || 0), 0),
          totalCost: tractSummariesGrouped[td].reduce((total, t) => total + (t.totalCost || 0), 0),
          dbh: this.calculateWeightedAverageForColumn(tractSummariesGrouped[td], 'dbh', 'netMbf'),
          rpi: this.calculateWeightedAverageForColumn(tractSummariesGrouped[td], 'rpi', 'netMbf'),
          age: this.calculateWeightedAverageForColumn(tractSummariesGrouped[td], 'age', 'netMbf'),
          tracts: tractSummariesGrouped[td]
        }
      }).sort((supplierOne, supplierTwo) => this.sortSuppliers(supplierOne, supplierTwo, bySpecies))
        : Object.keys(tractSummariesGrouped).map(td => {
          return {
            supplier: td !== null && td !== undefined && td !== 'null' ? td : this.$t('noAssignedSupplier'),
            loads: tractSummariesGrouped[td].reduce((total, t) => total + (t.loads || 0), 0),
            percentageOfTotal: tractSummariesGrouped[td].reduce((total, t) => total + (t.percentageOfTotal || 0), 0),
            grossWeight: tractSummariesGrouped[td].reduce((total, t) => total + (t.grossWeight || 0), 0),
            netWeight: tractSummariesGrouped[td].reduce((total, t) => total + (t.netWeight || 0), 0),
            totalCost: tractSummariesGrouped[td].reduce((total, t) => total + (t.totalCost || 0), 0),
            plf: this.calculateWeightedAverageForColumn(tractSummariesGrouped[td], 'plf'),
            dbh: this.calculateWeightedAverageForColumn(tractSummariesGrouped[td], 'dbh'),
            rpi: this.calculateWeightedAverageForColumn(tractSummariesGrouped[td], 'rpi'),
            age: this.calculateWeightedAverageForColumn(tractSummariesGrouped[td], 'age'),
            tracts: tractSummariesGrouped[td]
          }
        }).sort((supplierOne, supplierTwo) => this.sortSuppliers(supplierOne, supplierTwo, bySpecies))
    },

    calculateWeightedAverageForColumn (dataSet, attributeValue, weightedBy = 'netWeight') {
      const filteredData = dataSet.filter(ds => ds[attributeValue] !== 0)
      if (filteredData.length === 0) return undefined
      const sum = sumBy(filteredData, (d) => d[attributeValue] * d[weightedBy])
      const total = sumBy(filteredData, (d) => d[weightedBy])
      return total === 0 ? 0 : sum / total
    },

    sortSuppliers (supplierOne, supplierTwo, bySpecies) {
      if (supplierOne.supplier === this.$t('noAssignedSupplier')) return 1
      else if (supplierTwo.supplier === this.$t('noAssignedSupplier')) return -1

      const nameComparison = localeCompareAlphanumeric(supplierOne.supplier, supplierTwo.supplier)
      if (nameComparison !== 0) return nameComparison
      else return bySpecies ? supplierTwo.grossMbf - supplierOne.grossMbf : supplierTwo.grossWeight - supplierOne.grossWeight
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.focusedTractSummary = null
    }
  }
}
</script>
